import { useEffect, useRef, useState } from 'react';

// A separate same-origin browser window (window.open) React content portals
// into. Unlike the Document PiP window it replaced (JS-1249), a plain window
// has no browser-imposed size cap (PiP stops at ~80 % of the screen) — it can
// be maximized or snapped across the whole screen; the trade-off is that it
// is not always-on-top.
export const usePopoutWindow = () => {
  const [popoutWindow, setPopoutWindow] = useState<Window | null>(null);
  const popoutWindowRef = useRef<Window | null>(null);
  popoutWindowRef.current = popoutWindow;

  const open = (width: number, height: number, title: string): void => {
    if (popoutWindow) {
      return;
    }

    const newWindow = window.open(
      '',
      '_blank',
      `popup=yes,width=${width},height=${height}`
    );

    // Blocked by a popup blocker — the trigger is a user click, so this is
    // rare; the content simply stays in the main window.
    if (!newWindow) {
      return;
    }

    newWindow.document.title = title;

    // The popout document starts empty — clone the app styles so the portaled
    // content (incl. Ionic CSS variables) renders like in the main window.
    document
      .querySelectorAll('style, link[rel="stylesheet"]')
      .forEach((node) => {
        newWindow.document.head.appendChild(node.cloneNode(true));
      });

    // Closed by the user (or the browser) — drop the portal target.
    newWindow.addEventListener('pagehide', () => setPopoutWindow(null));

    setPopoutWindow(newWindow);
  };

  const close = (): void => {
    popoutWindow?.close();
    setPopoutWindow(null);
  };

  // The browser does not tie the popout to its opener (unlike PiP) — close it
  // explicitly when the main window unloads or the owner unmounts (route
  // change), or it would linger as an orphaned window.
  useEffect(() => {
    const closePopout = (): void => popoutWindowRef.current?.close();

    window.addEventListener('pagehide', closePopout);

    return () => {
      window.removeEventListener('pagehide', closePopout);
      closePopout();
    };
  }, []);

  return { popoutWindow, open, close };
};
